import {
  BREAK_POLICY_MIN_BREAK_DURATION_IN_MINUTES,
  BREAK_POLICY_MIN_SHIFT_DURATION_IN_MINUTES,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { type BreakPolicyRule } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";

export type WorkplaceBreakPolicy = BreakPolicyRule[];

const DEFAULT_SHIFT_BREAK_RULE: BreakPolicyRule = {
  minShiftDurationInMinutes: BREAK_POLICY_MIN_SHIFT_DURATION_IN_MINUTES,
  breakDurationInMinutes: BREAK_POLICY_MIN_BREAK_DURATION_IN_MINUTES,
};

/**
 * Tier-aware break rule resolver. Mirrors `resolveShiftBreakRule` in backend-main
 * `src/modules/facilityProfile/services/resolve-shift-break-rule.ts`.
 */
export function resolveShiftBreakRule(params: {
  policy: WorkplaceBreakPolicy | undefined;
  durationInMinutes: number;
  requiresLunchBreak?: boolean;
}): BreakPolicyRule | undefined {
  const { policy, durationInMinutes, requiresLunchBreak = true } = params;

  if (!requiresLunchBreak) {
    return undefined;
  }

  if (isDefined(policy) && policy.length > 0) {
    return policy
      .filter((rule) => rule.minShiftDurationInMinutes < durationInMinutes)
      .toSorted((a, b) => b.minShiftDurationInMinutes - a.minShiftDurationInMinutes)[0];
  }

  return durationInMinutes > DEFAULT_SHIFT_BREAK_RULE.minShiftDurationInMinutes
    ? DEFAULT_SHIFT_BREAK_RULE
    : undefined;
}

/**
 * Convenience wrapper around `resolveShiftBreakRule` returning just the break
 * duration in minutes (0 when no rule applies).
 */
export function resolveShiftBreakDurationInMinutes(params: {
  policy: WorkplaceBreakPolicy | undefined;
  durationInMinutes: number;
  requiresLunchBreak?: boolean;
}): number {
  return resolveShiftBreakRule(params)?.breakDurationInMinutes ?? 0;
}
